import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { RefreshCw, Loader2, Cloud, CircleDot, Copy as CopyIcon } from "lucide-react";

/**
 * Onglet Admin — Cloud Copytrading (Cross-VPS)
 * Affiche :
 *   - Groupes actifs (Pairing Key) : nb masters, nb slaves, dernière activité, events 24h
 *   - Timeline events récents (filtre par pairing_key)
 *   - Auto-refresh 10s
 */
export const CloudCopytradingTab = ({ token }) => {
  const headers = useMemo(() => ({ headers: { "x-admin-token": token } }), [token]);

  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get("/copy/admin/groups", headers);
      setGroups(data.groups || []);
      setLastRefresh(new Date());
    } catch (e) {
      // silent
    }
  }, [headers]);

  const loadEvents = useCallback(async () => {
    try {
      const q = selectedKey ? `?pairing_key=${encodeURIComponent(selectedKey)}&limit=100` : "?limit=100";
      const { data } = await api.get(`/copy/admin/events${q}`, headers);
      setEvents(data.events || []);
    } catch (e) {
      // silent
    }
  }, [headers, selectedKey]);

  const refreshAll = useCallback(async () => {
    setBusy(true);
    await Promise.all([loadGroups(), loadEvents()]);
    setBusy(false);
  }, [loadGroups, loadEvents]);

  useEffect(() => {
    refreshAll();
    const iv = setInterval(refreshAll, 10000);
    return () => clearInterval(iv);
  }, [refreshAll]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(text);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch (_e) {
      /* clipboard unavailable */
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
  };

  const eventTypeColor = (t) => {
    if (t?.startsWith("OPEN")) return "text-emerald-400";
    if (t?.startsWith("CLOSE")) return "text-orange-400";
    if (t === "MODIFY_SLTP") return "text-cyan-400";
    if (t === "CANCEL_PENDING") return "text-zinc-400";
    return "text-white";
  };

  return (
    <div className="space-y-6" data-testid="tab-cloud-copytrading">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Cloud className="h-6 w-6 text-cyan-400" />
          <div>
            <h2 className="text-xl font-medium">Cloud Copytrading</h2>
            <p className="text-sm text-zinc-400">Cross-VPS · groupes actifs + timeline events</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          {lastRefresh && <span>Rafraîchi à {lastRefresh.toLocaleTimeString("fr-FR")}</span>}
          <span className="inline-flex items-center gap-1">
            <CircleDot className="h-3 w-3 text-emerald-400 animate-pulse" />
            Auto 10s
          </span>
          <button
            onClick={refreshAll}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded hover:border-white/30 disabled:opacity-50"
            data-testid="cloud-copy-refresh"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Actualiser
          </button>
        </div>
      </div>

      {/* Groupes actifs */}
      <div className="border border-white/[0.06] rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-white/[0.02] border-b border-white/[0.06] text-sm text-zinc-300">
          Groupes actifs · <span className="font-mono text-cyan-400">{groups.length}</span>
        </div>
        {groups.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500" data-testid="cloud-copy-empty-groups">
            Aucun groupe actif — les Masters/Slaves apparaîtront ici dès leur premier heartbeat.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Pairing Key</th>
                  <th className="text-right px-4 py-2 font-medium">Masters</th>
                  <th className="text-right px-4 py-2 font-medium">Slaves</th>
                  <th className="text-right px-4 py-2 font-medium">Events 24h</th>
                  <th className="text-left px-4 py-2 font-medium">Dernière activité</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr
                    key={g.pairing_key}
                    className={`border-t border-white/[0.04] hover:bg-white/[0.02] ${selectedKey === g.pairing_key ? "bg-cyan-500/[0.05]" : ""}`}
                    data-testid={`cloud-copy-group-${g.pairing_key}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white">{g.pairing_key}</span>
                        <button
                          onClick={() => copyToClipboard(g.pairing_key)}
                          className="text-zinc-500 hover:text-white"
                          title="Copier"
                        >
                          <CopyIcon className="h-3 w-3" />
                        </button>
                        {copiedKey === g.pairing_key && <span className="text-xs text-emerald-400">Copié !</span>}
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 font-mono">
                      <span className={g.masters > 0 ? "text-emerald-400" : "text-zinc-600"}>{g.masters}</span>
                    </td>
                    <td className="text-right px-4 py-3 font-mono">
                      <span className={g.slaves > 0 ? "text-cyan-400" : "text-zinc-600"}>{g.slaves}</span>
                    </td>
                    <td className="text-right px-4 py-3 font-mono text-zinc-300">{g.events_24h}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{formatDate(g.last_activity)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedKey(selectedKey === g.pairing_key ? "" : g.pairing_key)}
                        className={`text-xs px-2 py-1 rounded border ${
                          selectedKey === g.pairing_key
                            ? "border-cyan-400 text-cyan-400 bg-cyan-500/10"
                            : "border-white/10 text-zinc-400 hover:border-white/30"
                        }`}
                        data-testid={`cloud-copy-filter-${g.pairing_key}`}
                      >
                        {selectedKey === g.pairing_key ? "✓ Sélectionné" : "Voir events"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Timeline events */}
      <div className="border border-white/[0.06] rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-white/[0.02] border-b border-white/[0.06] text-sm text-zinc-300 flex items-center justify-between">
          <div>
            Timeline · <span className="font-mono text-cyan-400">{events.length}</span> events
            {selectedKey && <span className="ml-2 text-xs text-zinc-500">(filtre : <span className="text-cyan-400 font-mono">{selectedKey}</span>)</span>}
          </div>
          {selectedKey && (
            <button
              onClick={() => setSelectedKey("")}
              className="text-xs text-zinc-400 hover:text-white"
              data-testid="cloud-copy-clear-filter"
            >
              Effacer filtre
            </button>
          )}
        </div>
        {events.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500" data-testid="cloud-copy-empty-events">
            Aucun event dans la dernière fenêtre 24h.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Timestamp</th>
                  <th className="text-left px-4 py-2 font-medium">Pairing</th>
                  <th className="text-left px-4 py-2 font-medium">Master</th>
                  <th className="text-left px-4 py-2 font-medium">Event</th>
                  <th className="text-left px-4 py-2 font-medium">Symbole</th>
                  <th className="text-left px-4 py-2 font-medium">Dir</th>
                  <th className="text-right px-4 py-2 font-medium">Vol</th>
                  <th className="text-right px-4 py-2 font-medium">Prix</th>
                  <th className="text-left px-4 py-2 font-medium">Stratégie</th>
                  <th className="text-right px-4 py-2 font-medium">Ticket</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={`${e.uuid}-${i}`} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`cloud-copy-event-${i}`}>
                    <td className="px-4 py-2 text-zinc-400 text-xs font-mono">{formatDate(e.created_at)}</td>
                    <td className="px-4 py-2 font-mono text-zinc-300">{e.pairing_key}</td>
                    <td className="px-4 py-2 font-mono text-zinc-300">{e.master_id}</td>
                    <td className={`px-4 py-2 font-mono ${eventTypeColor(e.event_type)}`}>{e.event_type}</td>
                    <td className="px-4 py-2 font-mono">{e.symbol || "—"}</td>
                    <td className={`px-4 py-2 font-mono ${e.direction === "BUY" ? "text-emerald-400" : e.direction === "SELL" ? "text-red-400" : "text-zinc-500"}`}>
                      {e.direction || "—"}
                    </td>
                    <td className="text-right px-4 py-2 font-mono text-zinc-300">{e.volume?.toFixed(2) || "—"}</td>
                    <td className="text-right px-4 py-2 font-mono text-zinc-400 text-xs">{e.price?.toFixed(5) || "—"}</td>
                    <td className="px-4 py-2 font-mono text-cyan-400 text-xs">{e.strategy || "—"}</td>
                    <td className="text-right px-4 py-2 font-mono text-zinc-500 text-xs">{e.master_ticket || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
